import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, Button, ButtonGroup, Card, Col, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import axios, { AxiosError, AxiosResponse } from "axios";
import Icon from "@mdi/react";
import { mdiAsterisk, mdiCheck, mdiCheckCircle, mdiChevronLeft, mdiCircleOutline, mdiCircleSlice8, mdiBookOutline, mdiRayStartArrow, mdiRayStartEnd, mdiRayStartVertexEnd } from "@mdi/js";
import { useMutation, useQuery } from "react-query";

import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

import {
	QuestionnaireResponseEntity,
	QuestionnaireResponsePostRequest,
	QuestionnaireResponsePostResponse,
	QuestionnaireResponsePutRequest,
	QuestionnaireResponsePutResponse,
	QuestionnaireResponseQuestionResponses,
} from "../../entities/QuestionnaireResponseEntity";
import { PerguntaTipo, QuestionnaireGetResponse, RespostaType } from "../../entities/QuestionnaireEntity";
import { QuestionGroupEntity } from "../../entities/QuestionGroupEntity";
import { QuestionEntity } from "../../entities/QuestionEntity";
import QuestionnaireResponseService from "../../services/QuestionnaireResponseService";
import QuestionnaireService from "../../services/QuestionnaireService";

import FormObjective from "src/components/FormObjective";
import FormParametro from "src/components/FormParametro";
import FormTextarea from "src/components/FormTextarea";
import FormNumber from "src/components/FormNumber";
import FormRadio from "src/components/FormRadio";
import FormCheck from "src/components/FormCheck";
import FormImage from "src/components/FormImage";
import FormText from "src/components/FormText";
import FormFile from "src/components/FormFile";
import FormDate from "src/components/FormDate";
import FormGeo from "src/components/FormGeo";
import FormCriteria, { FormCriteriaQuestionResponse } from "src/components/FormCriteria";
import Layout from "src/components/Layout";
import { getQuestionGroupColor } from "src/config/utils";

const toastTitle = "Avaliação";

function normalizeCriteriaQuestionResponse(response: QuestionnaireResponseQuestionResponses): RespostaType {
	return {
		...response,
		manifestations: response.manifestations ?? response.manifestationResponses ?? [],
	};
}

function getCriteriaManifestations(response: RespostaType) {
	return response.manifestations ?? response.manifestationResponses ?? [];
}

function buildIdField(id: number | undefined, includeIds: boolean) {
	return includeIds && id !== undefined ? { id } : {};
}

export default function AvaliacaoFormulario() {
	const questionnaireService = new QuestionnaireService();
	const questionnaireResponseService = new QuestionnaireResponseService();

	const navigate = useNavigate();
	const { handleToast } = useToast(toastTitle);
	const { user, geolocation } = useAuth();
	const { questionnaireId, id } = useParams();
	const [searchParams] = useSearchParams();

	const clientId = searchParams.get("clientId");
	const unitId = searchParams.get("unitId");

	const [formStatus, setFormStatus] = useState(false);
	const [formNavigationStatus, setFormNavigationStatus] = useState(false);
	const [formSaving, setFormSaving] = useState(false);
	const [formStage, setFormStage] = useState<{ grupo: number | null; pergunta: number | null }>({ grupo: null, pergunta: null });
	const [formRespostas, setFormRespostas] = useState<RespostaType[]>([]);
	const [criteriaWizardActive, setCriteriaWizardActive] = useState(false);
	const formNextRef = useRef(false);

	const { data, isLoading, isFetching, isFetched, isRefetching } = useQuery<QuestionnaireGetResponse>(["avaliacao", questionnaireId], () => fetchData(Number(questionnaireId)), {
		refetchOnWindowFocus: false,
	});
	const queryResponse = useQuery<QuestionnaireResponseEntity>(["avaliacaoResposta", id], () => fetchDataResponse(Number(id)), {
		enabled: id !== undefined && isFetched,
		refetchOnWindowFocus: false,
	});

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });
	void mutationPost;
	void mutationPut;

	useEffect(() => {
		if (formNextRef.current === true) {
			formNextRef.current = false;
			handleFormNext();
		}
	}, [formRespostas]);

	useEffect(() => {
		setCriteriaWizardActive(false);
	}, [formStage.grupo, formStage.pergunta]);

	async function fetchData(id: number) {
		let resp = await questionnaireService.get(id);
		let data = resp.data;
		data.questionGroups.sort((a, b) => {
			return a.order - b.order;
		});
		data.questionGroups.forEach((questionGroup) => {
			questionGroup.questions.sort((a, b) => {
				return a.order - b.order;
			});
		});
		return data;
	}

	async function fetchDataResponse(id: number) {
		let resp = await questionnaireResponseService.get(id);

		for (let i in resp.data.questionResponses) {
			let questionResponse = resp.data.questionResponses[i];

			for (let j in data?.questionGroups) {
				let questionGroup = data?.questionGroups[Number(j)];

				for (let k in questionGroup.questions) {
					let question = questionGroup.questions[Number(k)];

					if (question.id === questionResponse.questionId) {
						if (question.type === PerguntaTipo.criteria) {
							resp.data.questionResponses[i] = normalizeCriteriaQuestionResponse(questionResponse);
							questionResponse = resp.data.questionResponses[i];
						}

						if (question.type === PerguntaTipo.image || question.allowImage) {
							let response = resp.data.questionResponses[i] as RespostaType;
							if (response.files) {
								for (let i in response.files) {
									try {
										let file = response.files[i];
										let fileResp = await axios.get(file.fileUrl!, { responseType: "blob" });
										let fileData = URL.createObjectURL(fileResp.data);
										file.data = fileData;
										response.files[i] = file;
									} catch (e) {}
								}
								resp.data.questionResponses[i] = response;
							}
						}
					}
				}
			}

			if (questionResponse.optionResponses) {
				for (let j in questionResponse.optionResponses) {
					let optionResponse = questionResponse.optionResponses[j];
					let index = 0;
					for (let k in data?.questionGroups) {
						let questionGroup = data?.questionGroups[Number(k)];

						for (let l in questionGroup.questions) {
							let question = questionGroup.questions[l];

							if (question.id === questionResponse.questionId) {
								for (let m in question.optionGroups) {
									let optionGroup = question.optionGroups[m];

									if (
										optionGroup.options.some((option) => {
											return option.id === optionResponse.optionId;
										})
									) {
										index = Number(m);
									}
								}
							}
						}
					}
					resp.data.questionResponses[i].optionResponses![j].index = index;
				}
			}
		}
		setFormRespostas(resp.data.questionResponses);

		return resp.data;
	}

	async function mutatePost(data: QuestionnaireResponsePostRequest) {
		setFormSaving(true);
		return await questionnaireResponseService.post(Number(questionnaireId), data);
	}

	function mutatePostSuccess(resp: AxiosResponse<QuestionnaireResponsePostResponse, any>) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		navigate("/avaliacaoDetalhes/" + resp.data.id);
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: QuestionnaireResponsePutRequest) {
		setFormSaving(true);
		return await questionnaireResponseService.put(Number(id), data);
	}

	function mutatePutSuccess(resp: AxiosResponse<QuestionnaireResponsePutResponse, any>) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		navigate("/avaliacaoDetalhes/" + resp.data.id);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	function handleFormPrevious() {
		setFormNavigationStatus(true);
		if (formStage.grupo === null) {
			return;
		}
		let anterior: any;
		if (formStage.pergunta === null) {
			if (formStage.grupo === 0) {
				anterior = { grupo: null, pergunta: null };
			} else {
				anterior = handleFormDependenciesPrevious(formStage.grupo! - 1, questionGroups[formStage.grupo! - 1].questions.length - 1);
			}
		} else if (formStage.pergunta === 0) {
			anterior = { grupo: formStage.grupo, pergunta: null };
		} else {
			anterior = handleFormDependenciesPrevious(formStage.grupo, formStage.pergunta! - 1);
		}
		setFormStage({ grupo: anterior.grupo, pergunta: anterior.pergunta });
		setFormNavigationStatus(false);
	}

	function handleFormDependenciesPrevious(
		grupoIndex: number,
		perguntaIndex: number
	): {
		grupo: number | null;
		pergunta: number | null;
	} {
		let grupo = questionGroups[grupoIndex];

		let dependenciesCheck = grupo.questions.some((question) => {
			if (question.dependencyGroups) {
				return question.dependencyGroups.every((dependencyGroup) => {
					if (dependencyGroup.dependencies) {
						return dependencyGroup.dependencies.some((dependency) => {
							return formRespostas.some((resposta) => {
								return resposta.optionResponses?.some((response) => {
									return response.optionId === dependency.optionId;
								});
							});
						});
					}
					return true;
				});
			}
			return true;
		});

		if (!dependenciesCheck) {
			if (perguntaIndex === null) {
				if (grupoIndex === 0) {
					return { grupo: null, pergunta: null };
				} else {
					return handleFormDependenciesPrevious(grupoIndex - 1, questionGroups[grupoIndex - 1].questions.length - 1);
				}
			} else if (perguntaIndex === 0) {
				return handleFormDependenciesPrevious(grupoIndex - 1, questionGroups[grupoIndex - 1].questions.length - 1);
			} else {
				return handleFormDependenciesPrevious(grupoIndex, perguntaIndex - 1);
			}
		}

		return {
			grupo: grupoIndex,
			pergunta: perguntaIndex,
		};
	}

	async function handleFormNext() {
		setFormNavigationStatus(true);
		let respostas = [...formRespostas];
		if (data && formStage.grupo !== null && formStage.pergunta !== null) {
			let pergunta = questionGroups[formStage.grupo].questions[formStage.pergunta];
			if (pergunta.type === PerguntaTipo.file || pergunta.type === PerguntaTipo.image || (pergunta.type === PerguntaTipo.parametro && pergunta.allowImage)) {
				let respostaIndex = respostas.findIndex((item) => {
					return item.questionId === pergunta.id;
				});
				if (respostaIndex >= 0) {
					let resposta = respostas[respostaIndex];
					if (resposta && resposta.files) {
						let files = resposta.files;
						for (let i in resposta.files) {
							let file = resposta.files[i];
							if (file.id === undefined && file.blob) {
								try {
									const formData = new FormData() as any;
									formData.append("file", file.blob);

									let respFile = await questionnaireResponseService.postQuestionFile(formData);

									file.fileId = respFile.data;
									resposta.files[i] = file;
								} catch (_) {}
							} else if (file.id === undefined) {
								files.splice(Number(i), 1);
							}
						}
						resposta.files = files;
					}
					respostas[respostaIndex] = resposta;
					setFormRespostas(respostas);
				}
			}
		}

		let proxima = handleFormDependenciesNext(formStage.grupo!, formStage.pergunta !== null ? formStage.pergunta + 1 : 0, respostas);

		if (proxima.grupo === null && proxima.pergunta === null) {
			setFormStatus(true);
		}

		setFormStage({ grupo: proxima.grupo, pergunta: proxima.pergunta });
		setFormNavigationStatus(false);
	}

	function handleFormDependenciesNext(
		grupoIndex: number,
		perguntaIndex: number,
		respostas: RespostaType[]
	): {
		grupo: number | null;
		pergunta: number | null;
	} {
		let grupo = questionGroups[grupoIndex];
		if (!grupo) {
			return {
				grupo: null,
				pergunta: null,
			};
		}

		let pergunta = grupo.questions[perguntaIndex];
		if (!pergunta) {
			let resp = handleFormDependenciesNext(grupoIndex + 1, 0, respostas);
			if (resp.grupo !== null && resp.pergunta !== null) {
				return {
					grupo: resp.grupo,
					pergunta: null,
				};
			}
			return resp;
		}

		let dependenciesCheck = grupo.questions.some((question) => {
			if (question.dependencyGroups) {
				return question.dependencyGroups.every((dependencyGroup) => {
					if (dependencyGroup.dependencies) {
						return dependencyGroup.dependencies.some((dependency) => {
							return respostas.some((resposta) => {
								return resposta.optionResponses?.some((response) => {
									return response.optionId === dependency.optionId;
								});
							});
						});
					}
					return true;
				});
			}
			return true;
		});

		if (!dependenciesCheck) {
			return handleFormDependenciesNext(grupoIndex, perguntaIndex + 1, respostas);
		}

		if (pergunta) {
			let dependenciesCheckQuestion = pergunta.dependencyGroups.every((dependencyGroup) => {
				if (dependencyGroup.dependencies) {
					return dependencyGroup.dependencies.some((dependency) => {
						return respostas.some((resposta) => {
							return resposta.optionResponses?.some((response) => {
								return response.optionId === dependency.optionId;
							});
						});
					});
				}
				return true;
			});

			if (!dependenciesCheckQuestion) {
				return handleFormDependenciesNext(grupoIndex, perguntaIndex + 1, respostas);
			}
		}

		return {
			grupo: grupoIndex,
			pergunta: perguntaIndex,
		};
	}

	function handleQuestionGroupDependenciesCheck(grupo: QuestionGroupEntity) {
		return grupo.questions.some((question) => {
			if (question.dependencyGroups) {
				return question.dependencyGroups.every((dependencyGroup) => {
					if (dependencyGroup.dependencies) {
						return dependencyGroup.dependencies.some((dependency) => {
							return formRespostas.some((resposta) => {
								return resposta.optionResponses?.some((response) => {
									return response.optionId === dependency.optionId;
								});
							});
						});
					}
					return true;
				});
			}
			return true;
		});
	}

	function handleQuestionDependenciesCheck(question: QuestionEntity) {
		return question.dependencyGroups.every((dependencyGroup) => {
			if (dependencyGroup.dependencies) {
				return dependencyGroup.dependencies.some((dependency) => {
					return formRespostas.some((resposta) => {
						return resposta.optionResponses?.some((response) => {
							return response.optionId === dependency.optionId;
						});
					});
				});
			}
			return true;
		});
	}

	function handleFormText(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
	}

	function handleFormNumber(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
	}

	function handleFormTextarea(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
	}

	function handleFormRadio(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
		if (value.optionResponses?.length === perguntaAtual!.optionGroups?.length) {
			// handleFormNext();
			formNextRef.current = true;
		}
	}

	function handleFormCheck(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
	}

	function handleFormObjective(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
		// handleFormNext();
		formNextRef.current = true;
	}

	function handleFormGeoChange(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
	}

	function handleFormCriteria(questionId: number, questionResponses: FormCriteriaQuestionResponse[]) {
		setFormRespostas((p) => {
			const otherResponses = p.filter((response) => Number(response.questionId) !== Number(questionId));
			return [...otherResponses, ...questionResponses.map((response) => normalizeCriteriaQuestionResponse(response))];
		});
	}

	function handleFormParametro(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return Number(item.questionId) === Number(value.questionId);
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
		if (
			value.optionResponses?.length === perguntaAtual!.optionGroups?.length &&
			(perguntaAtual?.allowImage === false || (perguntaAtual?.allowImage === true && value.files && value.files[0].blob))
		) {
			// handleFormNext();
			formNextRef.current = true;
		}
	}

	function handleFormDate(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
	}

	function handleFormFileChange(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
		if (value.files?.length === perguntaAtual!.maxAnswers) {
			// handleFormNext();
			formNextRef.current = true;
		}
	}

	function handleFormImageChange(value: RespostaType) {
		setFormRespostas((p) => {
			let respostas = [...p];
			let respostaIndex = p.findIndex((item) => {
				return item.questionId === value.questionId;
			});
			if (respostaIndex >= 0) {
				respostas[respostaIndex] = value;
			} else {
				respostas.push(value);
			}
			return respostas;
		});
		if (value.files?.length === perguntaAtual!.maxAnswers) {
			// handleFormNext();
			formNextRef.current = true;
		}
	}

	const questionGroups = (data?.questionGroups ?? [])
		.map((questionGroup) => ({
			...questionGroup,
			questions: [...questionGroup.questions].sort((a, b) => {
				return a.order - b.order;
			}),
		}))
		.sort((a, b) => {
			return a.order - b.order;
		});

	let grupoAtual = null;
	if (formStage.grupo !== null && questionGroups[formStage.grupo]) {
		grupoAtual = questionGroups[formStage.grupo];
	}
	const grupoAtualColor = getQuestionGroupColor(grupoAtual?.color);
	let perguntaAtual: QuestionEntity | null = null;
	let perguntaAtualResposta: RespostaType | null = null;
	let perguntaAtualNext = true;
	if (formStage.grupo !== null && formStage.pergunta !== null && questionGroups[formStage.grupo].questions[formStage.pergunta]) {
		perguntaAtual = questionGroups[formStage.grupo].questions[formStage.pergunta];
		let perguntaId = questionGroups[formStage.grupo].questions[formStage.pergunta].id;
		perguntaAtualResposta =
			formRespostas.find((item) => {
				return item.questionId === perguntaId;
			}) ?? null;
		if (perguntaAtual.required) {
			perguntaAtualNext = false;
			if (perguntaAtualResposta) {
				perguntaAtualNext = true;
				if (perguntaAtualResposta.optionResponses && perguntaAtual.optionGroups) {
					perguntaAtualNext = perguntaAtualResposta.optionResponses.length === perguntaAtual.optionGroups.length;
					if (perguntaAtual.maxAnswers) {
						perguntaAtualNext = perguntaAtualResposta.optionResponses.length > 0;
					}
				}
			}
		}
		if (perguntaAtual.type === PerguntaTipo.criteria) {
			perguntaAtualNext = formRespostas.some((resposta) => {
				return Number(resposta.questionId) === Number(perguntaAtual!.id) && getCriteriaManifestations(resposta).length > 0;
			});
		}
	}
	const shouldHideQuestionNavigation =
		perguntaAtual?.type === PerguntaTipo.criteria &&
		(criteriaWizardActive ||
			!formRespostas.some((resposta) => {
				return Number(resposta.questionId) === Number(perguntaAtual!.id) && getCriteriaManifestations(resposta).length > 0;
			}));

	function buildQuestionResponsesPayload(isUpdate: boolean) {
		return formRespostas.map((resposta) => {
			const files = (resposta.files?.filter((item) => item !== undefined) ?? []).map((file) => ({
				...buildIdField(file.id, isUpdate),
				fileId: file.fileId,
				fileName: file.fileName,
				contentType: file.contentType,
			}));
			const optionResponses = (resposta.optionResponses ?? []).map((optionResponse) => ({
				...buildIdField(optionResponse.id, isUpdate),
				optionId: optionResponse.optionId,
			}));
			const groups = (resposta.groups ?? []).map((group) => ({
				...buildIdField(group.id, isUpdate),
				criteriaGroupId: group.criteriaGroupId,
				criteriaGroupTitle: group.criteriaGroupTitle,
			}));
			const classifications = (resposta.classifications ?? []).map((classification) => ({
				...buildIdField(classification.id, isUpdate),
				criteriaClassificationId: classification.criteriaClassificationId,
				criteriaClassificationTitle: classification.criteriaClassificationTitle,
			}));
			const manifestations = getCriteriaManifestations(resposta).map((manifestation) => ({
				...buildIdField(manifestation.id, isUpdate),
				manifestationId: manifestation.manifestationId,
				manifestationTitle: manifestation.manifestationTitle,
				risks: (manifestation.risks ?? []).map((risk) => ({
					...buildIdField(risk.id, isUpdate),
					riskId: risk.riskId,
					riskTitle: risk.riskTitle,
				})),
				recomendations: (manifestation.recomendations ?? []).map((recomendation) => ({
					...buildIdField(recomendation.id, isUpdate),
					recomendationId: recomendation.recomendationId,
					recomendationTitle: recomendation.recomendationTitle,
				})),
				domains: (manifestation.domains ?? []).map((domain) => ({
					...buildIdField(domain.id, isUpdate),
					domainId: domain.domainId,
					domainTitle: domain.domainTitle,
					domainOptionId: domain.domainOptionId,
					domainOptionTitle: domain.domainOptionTitle,
				})),
				files: (manifestation.files ?? []).map((file, fileIndex) => ({
					...buildIdField(file.id, isUpdate),
					fileId: file.fileId,
					fileName: file.fileName,
					contentType: file.contentType,
					caption: file.caption,
					order: file.order ?? fileIndex,
				})),
			}));
			const payload = {
				...buildIdField(resposta.id, isUpdate),
				questionId: resposta.questionId,
				value: resposta.value,
				text: resposta.text,
				complement: resposta.complement,
				geoLocation: resposta.geoLocation,
				optionResponses,
				files,
				groups,
				classifications,
			};

			if (isUpdate) {
				return {
					...payload,
					manifestationResponses: manifestations,
				};
			}

			return {
				...payload,
				manifestations,
			};
		});
	}

	return (
		<Layout sidebar={true}>
			<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
				<Link to={"/avaliacoes"} className="d-flex align-items-center text-decoration-none">
					<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiBookOutline} size={1} className="me-1" /> Formulário de Avaliação
				</Link>
				{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
			</h5>

			<Form
				onSubmit={(e) => {
					e.preventDefault();
					if (id) {
						const payload = {
							geoLocation: {
								latitude: geolocation?.coords.latitude ?? 0,
								longitude: geolocation?.coords.longitude ?? 0,
							},
							personId: user!.usuario.id,
							questionResponses: buildQuestionResponsesPayload(true),
						};

						console.log("Questionnaire response payload", payload);
						mutationPut.mutate(payload);
					} else {
						const payload = {
							geoLocation: {
								latitude: geolocation?.coords.latitude ?? 0,
								longitude: geolocation?.coords.longitude ?? 0,
							},
							personId: user!.usuario.id,
							clientId: clientId ? Number(clientId) : undefined,
							unitId: unitId ? Number(unitId) : undefined,
							questionResponses: buildQuestionResponsesPayload(false),
						};

						console.log("Questionnaire response payload", payload);
						mutationPost.mutate(payload);
					}
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter" && e.shiftKey === false) {
						e.preventDefault();
					}
				}}
			>
				{/* FORMULÁRIO */}
				{!formStatus && (
					<>
						{/* AVALIAÇÃO */}
						{grupoAtual === null && (
							<Row className="mb-4">
								<Col md={12}>
									<Card className="mb-4 rounded-3 shadow-sm">
										<Card.Body className="d-flex flex-column justify-content-center px-5 pb-4">
											<div className={`h2 mb-4 text-center ${isLoading ? "skeleton skeleton-text skeleton-text-title skeleton-center" : ""}`}>
												{isLoading ? "..." : data?.title}
											</div>
											<div className={`fw-light text-center fs-5 ${isLoading ? "skeleton skeleton-text skeleton-text-content skeleton-center" : ""}`}>
												{isLoading ? (
													<>
														<br />
														<br />
														<br />
														<br />
													</>
												) : (
													data?.purpose
												)}
											</div>
										</Card.Body>
									</Card>
									{!isLoading && !queryResponse.isLoading ? (
										<div className="d-flex justify-content-center">
											<Button
												size="lg"
												className="d-flex justify-content-center align-items-center gap-3 text-uppercase shadow-sm"
												onClick={() => {
													setFormStage({ grupo: 0, pergunta: null });
												}}
												style={{ width: 180 }}
											>
												{id ? "Editar" : "Começar"}
												<Icon path={mdiRayStartVertexEnd} size={1} />
											</Button>
										</div>
									) : (
										<div className="d-flex align-items-center justify-content-center text-muted gap-3 fs-5 p-2">
											<Spinner />
											Carregando {id !== undefined ? "Respostas" : ""}
										</div>
									)}
								</Col>
							</Row>
						)}

						{/* AVALIAÇÃO GRUPO / PERGUNTA */}
						{grupoAtual !== null && (
							<Row className="mb-4">
								{/* AVALIAÇÃO CABEÇALHO */}
								<Col md={12}>
									<Card className="mb-4 rounded-3">
										<Card.Body
											className="h2 text-center p-3 mb-0"
											onClick={() => {
												setFormStage({ grupo: null, pergunta: null });
											}}
											style={{ cursor: "pointer" }}
										>
											{data?.title}
										</Card.Body>
									</Card>
								</Col>

								{/* SUMARIO */}
								<Col md={3}>
									<Card className="mb-4 rounded-3 position-sticky overflow-auto" style={{ top: "0.5rem", maxHeight: "calc(100dvh - 1rem)" }}>
										<Card.Body className="d-flex flex-column">
											{questionGroups &&
												questionGroups
													.map((questionGroup, questionGroupIndex) => {
														const questionGroupColor = getQuestionGroupColor(questionGroup.color);
														let dependenciesCheck = handleQuestionGroupDependenciesCheck(questionGroup);
														if (!dependenciesCheck) {
															return null;
														}

														let response = questionGroup.questions.some((question) => {
															return formRespostas.some((resposta) => {
																return resposta.questionId === question.id;
															});
														});
														let active = questionGroupIndex === formStage.grupo;
														let enabled = questionGroupIndex < formStage.grupo! || response;
														let icon = mdiCircleOutline;
														let text = "text-gray-500";
														if (active) {
															icon = mdiCircleSlice8;
															text = "";
														} else if (enabled) {
															icon = mdiCheckCircle;
															text = "";
														}

														return (
															<div key={questionGroupIndex} className="d-flex flex-column">
																<div
																	className={`d-flex align-items-center p-1 rounded-3 text-decoration-none ${active ? "bg-nav-active" : ""} ${text}`}
																	onClick={() => {
																		setFormStage({ grupo: questionGroupIndex, pergunta: null });
																	}}
																	style={{ cursor: "pointer" }}
																>
																	<Icon path={icon} size={0.8} style={{ color: questionGroupColor }} className="me-1" />
																	<div style={{ flexBasis: 0, flexGrow: 1 }}>{questionGroup.title}</div>
																</div>
																{questionGroup.questions
																	.map((question, questionIndex) => {
																		let dependenciesCheck = handleQuestionDependenciesCheck(question);
																		if (!dependenciesCheck) {
																			return null;
																		}

																		let response = formRespostas.some((resposta) => {
																			return resposta.questionId === question.id;
																		});
																		let active = questionGroupIndex === formStage.grupo && questionIndex === formStage.pergunta;
																		let enabled =
																			questionGroupIndex < formStage.grupo! ||
																			(questionGroupIndex === formStage.grupo! && questionIndex <= formStage.pergunta!) ||
																			response;
																		let icon = mdiCircleOutline;
																		let text = "text-gray-500";
																		if (active) {
																			icon = mdiCircleSlice8;
																			text = "";
																		} else if (enabled) {
																			icon = mdiCheckCircle;
																			text = "";
																		}

																		return (
																			<div
																				key={questionIndex}
																				className={`d-flex align-items-center fw-light small p-1 rounded-3 text-decoration-none ${
																					active ? "bg-nav-active" : ""
																				} ${text}`}
																				onClick={() => {
																					setFormStage({ grupo: questionGroupIndex, pergunta: questionIndex });
																				}}
																				style={{ cursor: "pointer" }}
																			>
																				<Icon path={icon} size={0.8} style={{ color: questionGroupColor }} className="me-1" />
																				<div style={{ flexBasis: 0, flexGrow: 1 }}>{question.title}</div>
																			</div>
																		);
																	})}
															</div>
														);
													})}
										</Card.Body>
									</Card>
								</Col>

								{/* GRUPO */}
								{perguntaAtual === null && (
									<Col>
										<Card className="mb-4 rounded-3 shadow-sm" style={{ borderWidth: 4, borderStyle: "solid", borderColor: grupoAtualColor }}>
											<Card.Body className="d-flex flex-column justify-content-center p-5">
												<div className="h2 mb-4 text-center">{grupoAtual.title}</div>
												<div className="fw-light text-center text-muted mb-3">{grupoAtual.text}</div>
											</Card.Body>
										</Card>
										<div className="d-flex justify-content-center">
											<ButtonGroup size="lg" className="shadow-sm">
												<Button
													variant="light"
													className="d-flex justify-content-center align-items-center gap-3 text-uppercase"
													onClick={handleFormPrevious}
													disabled={formNavigationStatus}
													style={{ width: 180 }}
												>
													<Icon path={mdiRayStartArrow} size={1} style={{ transform: "scaleX(-1)" }} />
													Voltar
												</Button>
												<Button
													className="d-flex justify-content-center align-items-center gap-3 text-uppercase"
													onClick={handleFormNext}
													disabled={formNavigationStatus}
													style={{ width: 180 }}
												>
													Continuar
													<Icon path={mdiRayStartArrow} size={1} />
												</Button>
											</ButtonGroup>
										</div>
									</Col>
								)}

								{/* PERGUNTA */}
								{perguntaAtual !== null && (
									<Col>
										{/* PERGUNTA */}
										<Card className="mb-4 rounded-3 shadow-sm" style={{ borderTopWidth: 4, borderTopStyle: "solid", borderTopColor: grupoAtualColor }}>
											<Card.Body className="p-4">
												<div className="d-flex justify-content-center align-items-center h4 mb-2 text-center fw-normal">
													{perguntaAtual.title}
													{perguntaAtual.required === true && (
														<OverlayTrigger overlay={<Tooltip>É obrigatório responder</Tooltip>}>
															<div className="d-flex justify-content-center">
																<Icon path={mdiAsterisk} size={1} />
															</div>
														</OverlayTrigger>
													)}
												</div>
												<div className="fw-light text-center text-muted mb-3">{perguntaAtual.text}</div>
												{/* CAMPO TEXT */}
												{perguntaAtual.type === PerguntaTipo.text && (
													<FormText
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormText(value);
														}}
													/>
												)}
												{/* CAMPO TEXTAREA */}
												{perguntaAtual.type === PerguntaTipo.textarea && (
													<FormTextarea
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormTextarea(value);
														}}
													/>
												)}
												{/* CAMPO NUMBER */}
												{perguntaAtual.type === PerguntaTipo.number && (
													<FormNumber
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormNumber(value);
														}}
													/>
												)}
												{/* CAMPO RADIO */}
												{perguntaAtual.type === PerguntaTipo.radio && (
													<FormRadio
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormRadio(value);
														}}
													/>
												)}
												{/* CAMPO CHECKBOX */}
												{perguntaAtual.type === PerguntaTipo.checkbox && (
													<FormCheck
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormCheck(value);
														}}
													/>
												)}
												{/* CAMPO select */}

												{/* CAMPO DATE */}
												{perguntaAtual.type === PerguntaTipo.date && (
													<FormDate
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormDate(value);
														}}
													/>
												)}
												{/* CAMPO FILE */}
												{perguntaAtual.type === PerguntaTipo.file && (
													<FormFile
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormFileChange(value);
														}}
													/>
												)}
												{/* CAMPO IMAGE */}
												{perguntaAtual.type === PerguntaTipo.image && (
													<FormImage
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormImageChange(value);
														}}
													/>
												)}
												{/* CAMPO OBJECTIVE */}
												{perguntaAtual.type === PerguntaTipo.objective && (
													<FormObjective
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormObjective(value);
														}}
													/>
												)}
												{/* CAMPO GEO */}
												{perguntaAtual.type === PerguntaTipo.geo && (
													<FormGeo
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormGeoChange(value);
														}}
													/>
												)}
												{/* CAMPO PARAMETRO */}
												{perguntaAtual.type === PerguntaTipo.parametro && (
													<FormParametro
														key={perguntaAtual.id}
														pergunta={perguntaAtual}
														resposta={perguntaAtualResposta}
														onChange={(value) => {
															handleFormParametro(value);
														}}
													/>
												)}
												{/* CAMPO CRITÉRIO */}
												{perguntaAtual.type === PerguntaTipo.criteria && (
													<FormCriteria
														key={perguntaAtual.id}
														question={perguntaAtual}
														primaryColor={grupoAtualColor}
														onChange={(questionResponses) => handleFormCriteria(Number(perguntaAtual!.id), questionResponses)}
														onWizardActiveChange={setCriteriaWizardActive}
														onRequestPreviousQuestion={handleFormPrevious}
														initialQuestionResponses={
															formRespostas.filter((resposta) => Number(resposta.questionId) === Number(perguntaAtual!.id)) as FormCriteriaQuestionResponse[]
														}
													/>
												)}
											</Card.Body>
										</Card>
										{!shouldHideQuestionNavigation && <div className="d-flex justify-content-center">
											<ButtonGroup size="lg" className="shadow-sm">
												<Button
													variant="light"
													className="d-flex justify-content-center align-items-center gap-3 text-uppercase"
													onClick={handleFormPrevious}
													disabled={formNavigationStatus}
													style={{ width: 180 }}
												>
													<Icon path={mdiRayStartArrow} size={1} style={{ transform: "scaleX(-1)" }} />
													Anterior
												</Button>
												<Button
													className="d-flex justify-content-center align-items-center gap-3 text-uppercase"
													onClick={handleFormNext}
													disabled={!perguntaAtualNext || formNavigationStatus}
													style={{ width: 180 }}
												>
													Próximo
													<Icon path={mdiRayStartArrow} size={1} />
												</Button>
											</ButtonGroup>
										</div>}
									</Col>
								)}
							</Row>
						)}
					</>
				)}

				{/* SALVAR */}
				{formStatus && (
					<Row className="mb-4">
						<Col md={12}>
							<Card className="mb-4 rounded-3">
								<Card.Body
									className="h2 text-center p-3 mb-0"
									onClick={() => {
										setFormStage({ grupo: null, pergunta: null });
										setFormStatus(false);
									}}
									style={{ cursor: "pointer" }}
								>
									{data?.title}
								</Card.Body>
							</Card>
						</Col>

						<Col md={12}>
							<Card className="mb-4 rounded-3 shadow-sm">
								<Card.Body className="d-flex flex-column justify-content-center align-items-center text-center px-5 pb-4">
									{!formSaving && (
										<>
											<Icon path={mdiRayStartEnd} size={5} />
											<div className="fw-light text-center fs-4">Avaliação concluída</div>
										</>
									)}
									{formSaving && (
										<>
											<div className="d-flex justify-content-center align-items-center" style={{ width: 120, height: 120 }}>
												<Spinner style={{ width: 100, height: 100, borderWidth: 10 }} />
											</div>
											<div className="fw-light text-center fs-4">Salvando avaliação</div>
										</>
									)}
								</Card.Body>
							</Card>
							{!formSaving && (
								<div className="d-flex justify-content-center">
									<Button size="lg" type="submit" className="d-flex gap-2 align-items-center text-uppercase px-5 rounded-3 shadow-sm">
										Salvar
										<Icon path={mdiCheck} size={1} />
									</Button>
								</div>
							)}
						</Col>
					</Row>
				)}
			</Form>
		</Layout>
	);
}
